import { AppAuthOptionsList } from '../../auth';

export enum TimeHorizon {
  '1D' = '1d',
  '7D' = '7d',
  '30D' = '30d',
  '60D' = '60d',
  '90D' = '90d',
  '180D' = '180d',
  '1Y' = '1y',
  Full = 'full',
  Baseline = 'baseline',
}
export enum HealthStatus {
  None = 'none',
  Healthy = 'healthy',
  Sick = 'sick',
}
export enum ModelExecutionStage {
  Dev = 'dev',
  UAT = 'uat',
  Test = 'test',
  Prod = 'prod',
}
export type RunIntentType = 'dev-testing' | 'uat' | 'prod';

export enum ModelArtifactType {
  Pickle = 'pickle',
  Class = 'class',
  Requirements = 'requirements',
  Attributes = 'attributes',
  ModelFeaturesJson = 'model_features_json',
  Predictor = 'predictor',
  Benchmark = 'benchmark',
  CustomLibraries = 'custom_libs',
}
export enum ModelExecutionStatus {
  Queued = 'Queued',
  InService = 'InService',
  Failed = 'Failed',
  OutOfService = 'OutOfService',
  Creating = 'Creating',
  Updating = 'Updating',
  SystemUpdating = 'SystemUpdating',
  RollingBack = 'RollingBack',
  Deleting = 'Deleting',
}
export enum ModelFeatureType {
  Generic = 'generic',
  External = 'external',
  Marketing = 'marketing',
  Custom = 'custom',
}
export enum GoModelPurpose {
  Underwriting = 'underwriting',
  Marketing = 'marketing',
  AccountManagement = 'account_management',
  EcsActivate = 'ECS ACTIVATE',
}
export enum GoModelSubPurpose {
  PreScreen = 'Pre-Screen',
  ITA = 'ITA',
  LineAssignment = 'Line Assignment',
}
export enum GoModelProduct {
  CreditCard = 'Credit Card',
  Auto = 'Auto',
  Mortgage = 'Mortgage',
  HomeEquity = 'Home Equity',
  PersonalLending = 'Personal Lending',
  SmallBusiness = 'Small Business',
}
export enum GoModelUsecases {
  Commercial = 'Commercial',
  Consumer = 'Consumer',
}
export enum ExecutionType {
  Batch = 'batch',
  Realtime = 'realtime',
  Both = 'both',
}

export type ExecutionTypeForTestRun = Exclude<
  ExecutionType,
  ExecutionType.Both
>;

export enum ExecutionStatus {
  Processing = 'processing',
  Success = 'success',
  Failure = 'failure',
}
export enum GoModelBuildStatus {
  DRAFT = 'draft',
  TEST = 'test',
  REVIEW = 'review',
  APPROVED = 'approved',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  FAILED = 'failed',
  CREATED = 'created',
  SCANNING = 'scanning',
  DEPLOYING_TO_TEST = 'deploying-to-test',
  DEPLOYING_TO_PROD = 'deploying-to-prod',
  ARCHIVED = 'archived',
  RESTORING = 'restoring',
  UNKNOWN = 'unknown',
}

export enum GoModelStaticScanReportBuildStatus {
  FAILED = 'Failed',
  PENDING = 'Pending',
  PASS = 'Pass',
  UNKNOWN = 'unknown',
}

export enum GoModelType {
  Internal = 'internal',
  AIS = 'ais',
  Zest = 'zest',
  H2O = 'h2o',
  DataRobot = 'data_robot',
}

export const BUILD_STATUS_COLOR: { [key: string]: string } = {
  [GoModelBuildStatus.DRAFT]: 'primary.aquaHaze',
  [GoModelBuildStatus.TEST]: 'secondary.yellow',
  [GoModelBuildStatus.REVIEW]: 'secondary.orange',
  [GoModelBuildStatus.APPROVED]: 'primary.lightBlue',
  [GoModelBuildStatus.ACTIVE]: 'secondary.green',
  [GoModelBuildStatus.INACTIVE]: 'gray.gray',
  [GoModelBuildStatus.FAILED]: 'primary.magenta',
  [GoModelBuildStatus.CREATED]: 'primary.purple',
  [GoModelBuildStatus.DEPLOYING_TO_TEST]: 'secondary.lime',
  [GoModelBuildStatus.DEPLOYING_TO_PROD]: 'secondary.teal',
};

export type BackendGoModelInferencesGraphData = {
  category: string[];
  category_label: string;
  data: number[];
  data_label: string;
};
export enum BatchExecutionSubTypes {
  OndemandSufficientFile = 'ondemand-sufficient-file',
  Ondemand1Percent = 'ondemand-1percent',
  Ondemand100Percent = 'ondemand-100percent',
  OndemandPinFile = 'ondemand-pin-file',
  OndemandFile = 'ondemand-file',
  OndemandECSSnapshot = 'ondemand-ecs-snapshot',
}

export type BasicPrimitive = number | string | boolean;
export type InferenceQueryParam = { aggregation: TimeHorizon };
export type LanguageParam = { languageUid: string };
export type ClientParam = { clientUid: string };
export type ModelParam = { modelUid: string };
export type ActualFileParam = { file: File };
export type ExeHistoryParam = { modelUid: string; executionUid: string };
export type DatasetParam = { datasetUid: string };
export type FeatureParam = { datasetUid: string; featureUid: string };
export type FeatureConfigParam = { tenant: string; client?: string };
export type DataDriftParam = { modelUid: string; timeHorizon: TimeHorizon };
export type DataDriftFullParam = {
  modelUid: string;
  refTimeHorizon: TimeHorizon;
  compTimeHorizon: TimeHorizon;
  ci: string;
};
export type DocumentsParam = { modelUid: string; fileUid: string };
export type FileParam = { fileUid: string };
export type FileTagParam = { fileTag: string };
export type ModelNameParam = { name: string };
export type ExecutionLevelParam = { executionLevel: ModelExecutionStage };
export type MappingParam = { mappingUid: string };
export type DownloadBatchOutputParam = {
  executionUid: string;
};
export type ExecutionHistoryParam = {
  executionLevel?: ModelExecutionStage;
  modelUid?: string;
};
export type ExecutionAvailabilityParam = {
  executionMode: ExecutionTypeForTestRun;
  batchExecutionSubtype: BatchExecutionSubTypes;
};
export type TranslationParam = { lang: string; module: string };
export type ExecHistoryDetailParam = { executionUid: string };
export type BackendGoFileArtifact = {
  type: ModelArtifactType;
  location: string;
};
export interface ApiConfig {
  apiRoot: string;
  authApiRoot: string;
}

export interface AppConfig {
  useGlobalOktaSession: boolean;
  useLocalization: boolean;
  appStack: string;
  businessRegion: string;
  launchScriptSource: string;
  saasAssetsUrl?: string;
  allowedTenantsForSaaSHeader?: string[];
  idleSessionTimeInMs?: number;
}

export type AuthConfig = AppAuthOptionsList;
export interface ServerConfig {
  apiConfig: ApiConfig;
  authConfig: AuthConfig;
  appConfig: AppConfig;
}
